import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/synapse/Field';
import { Icon } from '@/components/synapse/Icon';
import { PhotoCapture, type CapturedPhoto } from '@/components/synapse/PhotoCapture';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import type { Patient } from '@/types/patients';
import { PatientDetailsFields } from './PatientDetailsFields';
import {
  emptyPatientDetails,
  hasPatientDetailsErrors,
  resolveDateOfBirth,
  validatePatientDetails,
  type PatientDetailsDraft,
  type PatientDetailsErrors,
} from './patientDetails';
import { usePatientPhotoUpload, useQuickCreatePatient, useUpdatePatient } from './usePatients';

interface QuickCreatePatientProps {
  initialName?: string;
  onCreated: (p: Patient) => void;
  onCancel: () => void;
}

/**
 * Quick-create form (spec Part 1 §3.4). The quick endpoint only accepts
 * `{ fullName, phone, sex }`; DOB and NIC are persisted via a follow-up PATCH on
 * the new id. When only an Age is given (no DOB), we persist an approximate
 * `dateOfBirth = (currentYear - age)-01-01` and tell the user it's approximate.
 *
 * The field set itself lives in PatientDetailsFields, shared with the public
 * self-registration page and the review of a submission.
 */
export function QuickCreatePatient({ initialName = '', onCreated, onCancel }: QuickCreatePatientProps) {
  const { t } = useTranslation();
  const quickCreate = useQuickCreatePatient();
  const updatePatient = useUpdatePatient();
  const uploadPhoto = usePatientPhotoUpload();

  const [details, setDetails] = useState<PatientDetailsDraft>(() =>
    emptyPatientDetails({ fullName: initialName }),
  );
  const [photo, setPhoto] = useState<CapturedPhoto | null>(null);

  // Revoke the captured photo's object URL when it's replaced or on unmount so a
  // bounced-around blob preview doesn't leak.
  useEffect(() => {
    return () => {
      if (photo) URL.revokeObjectURL(photo.previewUrl);
    };
  }, [photo]);
  const [fieldErrors, setFieldErrors] = useState<PatientDetailsErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Non-blocking warning: the patient WAS created, but the follow-up PATCH
  // (DOB/NIC) failed. Distinct from a create failure so the user isn't told to
  // resubmit (which would create a second patient).
  const [extrasWarning, setExtrasWarning] = useState<string | null>(null);

  const submitting = quickCreate.isPending || updatePatient.isPending || uploadPhoto.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    setExtrasWarning(null);
    const name = details.fullName.trim();
    // Validate BEFORE the create. Step 1 below writes the patient and step 3
    // PATCHes the NIC on, so a NIC the server would reject reaches the user as a
    // vague "extras didn't save" warning on an already-created record.
    const errors: PatientDetailsErrors = {
      ...validatePatientDetails(details),
      fullName: name ? null : t('patients.quickCreate.fullNameRequired'),
    };
    setFieldErrors(errors);
    if (hasPatientDetailsErrors(errors)) return;

    // Step 1: create the patient. A failure here keeps the form open with a
    // create-failed message so the user can retry the create.
    let created: Patient;
    try {
      created = await quickCreate.mutateAsync({
        fullName: name,
        phone: details.phone.trim() || undefined,
        sex: details.sex,
      });
    } catch (err) {
      setSubmitError(
        err instanceof ApiError ? translateApiError(err) : t('patients.quickCreate.createFailed'),
      );
      return;
    }

    // Explicit DOB wins; otherwise an age becomes an approximate 1-January date.
    // The quick endpoint ignores both, so they go through a follow-up PATCH.
    const effectiveDob = resolveDateOfBirth(details.dob, details.age);
    const nic = details.nationalId.trim();

    // Steps 2+ (optional): the patient ALREADY EXISTS now, so any failure here
    // must NOT strand the user or risk a double-create — we still navigate into
    // the created record and surface a non-blocking warning distinct from a
    // create failure. Each follow-up advances `current` so we open the freshest
    // version of the record.
    let current = created;
    let warning: string | null = null;

    // Photo: upload the captured blob directly to S3 (presigned) then confirm.
    if (photo) {
      try {
        current = await uploadPhoto.mutateAsync({
          patientId: current.id,
          blob: photo.blob,
          contentType: photo.contentType,
        });
      } catch {
        warning = t('patients.quickCreate.createdButPhotoFailed');
      }
    }

    // DOB / NIC.
    if (effectiveDob != null || nic) {
      try {
        current = await updatePatient.mutateAsync({
          id: current.id,
          patch: {
            ...(effectiveDob != null ? { dateOfBirth: effectiveDob } : {}),
            ...(nic ? { nationalId: nic } : {}),
          },
        });
      } catch {
        warning = t('patients.quickCreate.createdButExtrasFailed');
      }
    }

    if (warning) setExtrasWarning(warning);
    onCreated(current);
  }

  return (
    <form onSubmit={handleSubmit} className="flex w-full max-w-[560px] flex-col gap-[18px]">
      <Field label={t('patients.quickCreate.photo')} optional>
        {() => (
          <PhotoCapture
            name={details.fullName}
            previewUrl={photo?.previewUrl ?? null}
            onCapture={setPhoto}
            onRemove={() => setPhoto(null)}
            busy={submitting}
          />
        )}
      </Field>

      <PatientDetailsFields
        value={details}
        onChange={setDetails}
        errors={fieldErrors}
        autoFocusName
      />

      <div className="flex items-center gap-2.5 rounded-md border border-line bg-paper-2 px-3.5 py-3">
        <Icon name="info" size={16} className="shrink-0 text-info" />
        <span className="text-[12.5px] leading-snug text-muted">{t('patients.quickCreate.infoNote')}</span>
      </div>

      {submitError && (
        <p className="text-[12.5px] text-destructive" role="alert">
          {submitError}
        </p>
      )}

      {extrasWarning && (
        <p className="text-[12.5px] text-warn" role="alert">
          {extrasWarning}
        </p>
      )}

      <div className="flex items-center gap-2.5 border-t border-line pt-5">
        <span className="hidden text-[12px] text-muted sm:inline">
          {t('patients.quickCreate.subtitle')}
        </span>
        <div className="flex-1" />
        <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? t('patients.quickCreate.creating') : t('patients.quickCreate.create')}
          {!submitting && <Icon name="arrow_up_right" size={16} />}
        </Button>
      </div>
    </form>
  );
}
